import { ArrowSmRightIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import { savedTokens } from 'src/auth'
import {
  ErrorNotLoggedIn,
  Form,
  LocationInfo,
  Paragraph,
  StickyFooter,
  Title,
} from 'src/components'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import {
  savedSfUsername,
  savedSfPassword,
  savedSfClientId,
  savedSfClientSecret,
  savedSfButtonEnabled,
  savedSfButtonKey,
} from '../storage'
import {
  useEditOrganization,
  useSalesforceConnectionTest,
} from 'src/routes/Onboarding/queries'
import InputField from 'src/components/Shared/UIComponents/InputField'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import SwitchWithLabel from 'src/components/Shared/UIComponents/SwitchWithLabel'
import { toast } from 'sonner'

export default function Config() {
  const navigate = useNavigate()
  const [tokens] = savedTokens.useState(null)
  const [username, setUsername] = savedSfUsername.useState('')
  const [password, setPassword] = savedSfPassword.useState('')
  const [clientId, setClientId] = savedSfClientId.useState('')
  const [clientSecret, setClientSecret] = savedSfClientSecret.useState('')
  const [buttonEnabled, setButtonEnabled] = savedSfButtonEnabled.useState(true)
  const [buttonKey, setButtonKey] = savedSfButtonKey.useState('SALESFORCE1_URL')
  const editOrganization = useEditOrganization()
  const salesforceConnectionTest = useSalesforceConnectionTest()

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  return (
    <Form
      onSubmit={async (e) => {
        e.preventDefault()
        if (username && password && clientId && clientSecret) {
          await salesforceConnectionTest.mutateAsync({
            username: tokens.username,
            organization: tokens.organization,
            sf_username: username,
            sf_password: password,
            sf_clientId: clientId,
            sf_clientSecret: clientSecret,
          })

          await editOrganization.mutateAsync({
            application: {
              sf_username: username,
              sf_password: password,
              sf_client_id: clientId,
              sf_client_secret: clientSecret,
              sf_button_enabled: buttonEnabled,
              sf_button_key: buttonKey,
              salesforceEnabled: false,
            },
            setupStep: 'finished',
            username: tokens.username,
            organization: tokens.organization,
          })
          navigate('../finish')
        } else {
          toast.error('Please correctly fill in the form first.')
        }
      }}
    >
      <Title label="Salesforce User" />
      <LocationInfo path="Salesforce/Setup/Users/Users/New User" />

      <Paragraph className="col-span-full">
        To be able to insert data from the mobile application into salesforce,
        we need to configure an integration user. <br />
        If the current system already has an integration user, you can skip
        steps 1 to 8.
      </Paragraph>
      <ol className="mt-4 ml-4 col-span-full list-decimal text-base text-secondary leading-8">
        <li>Go to Salesforce &gt; Setup &gt; Users &gt; Users</li>
        <li>
          Click the <b>New User</b> button
        </li>
        <li>Fill in the required information</li>
        <li>
          User licence: <b>Salesforce Integration</b>
        </li>
        <li>
          Profile: <b>Salesforce API Only System Integration</b>
        </li>
        <li>
          Role: <b>Select a top level role</b>
        </li>
        <li>
          Click <b>Save</b> to create the user
        </li>
        <li>
          An email will be sent to verify and set the password of this user
        </li>
        <li>
          Assign this user the <b>Mobile Connect Data Integration</b> permission
          set and enter the username and password below.
        </li>
      </ol>

      <InputField
        label="Salesforce Username"
        placeholder="john.doe@gmail.com"
        value={username}
        setValue={setUsername}
      />

      <InputField
        label="Salesforce Password"
        placeholder="password"
        value={password}
        setValue={setPassword}
        type="password"
      />

      <Title label="Salesforce Connected App" className="mt-12" />
      <LocationInfo path="Salesforce/Setup/Apps/App Manager/New Connected App" />

      <Paragraph className="col-span-full">
        To create the connection between salesforce and the mobile app, a
        connected app will be created.
        <br />
        This conneced app will authenticate the user and authorize him to call
        the needed webservices.
      </Paragraph>

      <ol className="mt-4 ml-4 ml-7 col-span-full w-full list-decimal text-base text-secondary leading-8">
        <li>Go to Salesforce &gt; Setup &gt; Apps &gt; App manager</li>
        <li>
          Click the <b>New Connected app</b> button
        </li>
        <li>
          Connected App Name: <b>Mobile Connect</b>
        </li>
        <li>Under contact email put the email of the one responsible</li>
        <li>
          Click the <b>enable Oauth Settings</b> checkbox
        </li>
        <li>
          Callback URL: <b>https://www.salesforce.com/callback</b>
        </li>
        <li>
          Under <b>Selected Oauth Scopes</b>, select the following options:
          <ul className="list-disc">
            <li>
              <b>Manage user data via APIs (api)</b>
            </li>
            <li>
              <b>
                Perform requests at any time (refresh_token, offline_access)
              </b>
            </li>
          </ul>
        </li>
        <li>
          Click <b>Save</b> to create the connected App
        </li>
        <img
          className="border-2 mb-5 mt-2 col-span-full"
          src={require('src/routes/Salesforce/images/connected-app.png')}
          width="920"
          alt="login url"
        />
        <li>
          On the next screen, click the <b>Manage Consumer Details</b> button
        </li>
        <li className="">
          Please copy and paste the <b>Consumer key</b> and{' '}
          <b>Consumer Secret</b> value below.
        </li>
      </ol>

      <InputField
        label="Salesforce Client Id"
        placeholder="client id"
        value={clientId}
        setValue={setClientId}
      />

      <InputField
        label="Salesforce Client Secret"
        placeholder="client secret"
        value={clientSecret}
        setValue={setClientSecret}
        type="password"
      />

      <Title label="Salesforce Configuration" className="col-span-full mt-12" />

      <Paragraph className="col-span-full">
        When receiving interactions in the app, you can have a button that
        directly opens the contact in the Salesforce One app. You can configure
        the url it should open in your interaction flow, by default we use the
        following key: <b>SALESFORCE1_URL</b> but this can be changed to your
        liking below. If you're not sure about this, just click next as you'll
        still be able to change this later on.
      </Paragraph>

      <SwitchWithLabel
        label="Show Salesforce button"
        value={buttonEnabled}
        setValue={setButtonEnabled}
      />

      <InputField
        label="Salesforce Button Key"
        placeholder="SALESFORCE1_URL"
        value={buttonKey}
        setValue={setButtonKey}
      />

      <StickyFooter>
        <EmailHelpBox className="flex-1" />
        <CustomButton
          type="submit"
          label="next"
          icon={<ArrowSmRightIcon />}
          iconPosition="right"
        />
      </StickyFooter>
    </Form>
  )
}
